/**
 * Multi-kernel pool that manages all Jupyter kernels for a workspace.
 *
 * The pool is a dumb resource manager -- it knows how to start, stop,
 * list, and connect to kernels. It has no opinion about who is using
 * them or why. Consumers (Code Interface, Antioch Agent) sit above it.
 */

import { KernelAPI, KernelSpecAPI, ServerConnection, SessionAPI } from '@jupyterlab/services';
import * as Sentry from '@sentry/react';
import type { KernelConnectionStatus, KernelHealth, KernelHealthListener, KernelStatus } from './JupyterClient';
import { JupyterClient, KernelNotFoundError } from './JupyterClient';

export { KernelNotFoundError } from './JupyterClient';

/**
 * Thrown when a consumer tries to start or attach a kernel before the
 * KernelPool is connected to a workspace.
 *
 * This is an expected precondition failure (no workspace connected yet),
 * not an application bug. Callers should catch this and surface a
 * user-friendly message rather than forwarding it to Sentry as an
 * exception.
 */
export class KernelNotAvailableError extends Error {
  override readonly name = 'KernelNotAvailableError';

  constructor(message = 'KernelPool is not connected') {
    super(message);
  }
}

export interface KernelInfo {
  id: string;
  name: string;
  displayName: string;
  status: KernelStatus;
  connectionStatus: KernelConnectionStatus;
  consumers: Set<string>;
  startedAt: number;
}

/**
 * A kernelspec registered on the Jupyter server.
 *
 * Mirrors the relevant fields of the server's ``/api/kernelspecs`` entries.
 * The pool surfaces every registered spec so the UI can offer all of them
 * rather than assuming the workspace only ships ``antioch-sim``.
 */
export interface KernelSpecInfo {
  /** Spec name passed to the Jupyter API when starting a kernel (e.g. 'antioch-sim'). */
  name: string;
  /** Human-readable label from the spec's ``display_name`` (falls back to ``name``). */
  displayName: string;
  /** Kernel language (e.g. 'python'); empty when the server omits it. */
  language: string;
}

type KernelsChangedListener = (kernels: KernelInfo[]) => void;

export class KernelPool {
  private _serverSettings: ServerConnection.ISettings;
  private _token: string | undefined;
  private _kernels = new Map<string, { client: JupyterClient; info: KernelInfo }>();
  private _kernelSpecs: KernelSpecInfo[] = [];
  private _defaultKernelSpec: string | null = null;
  private _listeners = new Set<KernelsChangedListener>();
  private _disposed = false;
  private _onTokenRefreshNeeded: (() => Promise<string | undefined>) | null = null;
  // Deduplicates concurrent refresh() calls -- several providers auto-connect
  // and call refresh in parallel during workspace load, which triggered the
  // N+1 /jupyter/api/kernels pattern detected by Sentry.
  private _refreshInFlight: Promise<void> | null = null;

  constructor(baseUrl: string, token?: string) {
    this._serverSettings = ServerConnection.makeSettings({
      baseUrl,
      wsUrl: baseUrl.replace(/^http/, 'ws'),
      token: token ?? '',
      appendToken: !!token,
    });
    this._token = token;
  }

  get baseUrl(): string {
    return this._serverSettings.baseUrl;
  }

  get serverSettings(): ServerConnection.ISettings {
    return this._serverSettings;
  }

  /**
   * Update the authentication token used for Jupyter API requests.
   *
   * Called when ServiceTokensProvider refreshes the workspace JWT.
   * Rebuilds server settings so that subsequent API calls (starting kernels,
   * listing kernels, etc.) use the fresh token. Already-created JupyterClient
   * instances in the pool retain their original token — only new connections
   * use the updated credential. Existing WebSocket connections are unaffected.
   */
  updateToken(token?: string): void {
    if (token === this._token) return;
    this._token = token;
    this._serverSettings = ServerConnection.makeSettings({
      baseUrl: this._serverSettings.baseUrl,
      wsUrl: this._serverSettings.wsUrl,
      token: token ?? '',
      appendToken: !!token,
    });
  }

  /**
   * Register a callback invoked when a Jupyter API call returns 401.
   *
   * The callback should refresh the workspace token and return the new
   * value. The pool will call updateToken() with the result and retry
   * the failed operation once.
   */
  setTokenRefreshCallback(cb: (() => Promise<string | undefined>) | null): void {
    this._onTokenRefreshNeeded = cb;
  }

  /** List all known kernels and their consumer sets */
  listKernels(): KernelInfo[] {
    return Array.from(this._kernels.values()).map((e) => ({ ...e.info, consumers: new Set(e.info.consumers) }));
  }

  /**
   * Kernelspecs registered on the Jupyter server.
   *
   * Returns the cached list; call ``refreshKernelSpecs()`` to populate it.
   * Empty until the first refresh succeeds.
   */
  listKernelSpecs(): KernelSpecInfo[] {
    return this._kernelSpecs.map((s) => ({ ...s }));
  }

  /** Name of the server's default kernelspec, or null if not yet fetched. */
  get defaultKernelSpec(): string | null {
    return this._defaultKernelSpec;
  }

  /**
   * Fetch the kernelspecs registered on the Jupyter server (``/api/kernelspecs``).
   *
   * Kernelspecs are static for a workspace image, so the result is cached on
   * the pool and returned by ``listKernelSpecs()``. Surfaces every registered
   * spec so the UI can offer all of them, not just ``antioch-sim``.
   *
   * Best-effort and mirrors ``refresh()``: a failure (including a 401) keeps
   * the previous cache and resolves with whatever is cached rather than
   * throwing, so a transient kernelspec lookup never blocks kernel startup.
   */
  async refreshKernelSpecs(): Promise<KernelSpecInfo[]> {
    if (this._disposed) return this.listKernelSpecs();
    try {
      const specs = await KernelSpecAPI.getSpecs(this._serverSettings);
      if (this._disposed) return this.listKernelSpecs();
      this._defaultKernelSpec = specs.default ?? null;
      this._kernelSpecs = Object.values(specs.kernelspecs)
        .filter((spec): spec is NonNullable<typeof spec> => spec != null)
        .map((spec) => ({
          name: spec.name,
          displayName: spec.display_name || spec.name,
          language: spec.language || '',
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
      return this.listKernelSpecs();
    } catch (err) {
      // Best-effort and non-blocking, so this is a breadcrumb rather than a
      // captured issue: a failed lookup just leaves the kernel-type picker
      // showing the cached (often empty) list and never blocks kernel startup.
      // Breadcrumb every failure -- not only 401 -- because this list is what
      // powers the whole multi-kernelspec picker, so its narrative trail is
      // worth having on the next real error.
      Sentry.addBreadcrumb({
        category: 'jupyter',
        message: isJupyter401(err) ? 'kernelspec list refresh failed with 401' : 'kernelspec list refresh failed',
        level: 'warning',
        data: { error: err instanceof Error ? err.message : String(err) },
      });
      console.warn('[kernel-pool] Failed to refresh kernelspec list:', err);
      return this.listKernelSpecs();
    }
  }

  /**
   * Re-poll the Jupyter server for running kernels.
   *
   * Concurrent callers share a single in-flight request to prevent the N+1
   * pattern where JupyterProvider auto-connect, KernelPoolProvider
   * auto-connect, and token-refresh effects all fire refresh() together on
   * /workspace mount.
   */
  async refresh(): Promise<void> {
    if (this._disposed) return;
    if (this._refreshInFlight) return this._refreshInFlight;

    const task = this._doRefresh().finally(() => {
      this._refreshInFlight = null;
    });
    this._refreshInFlight = task;
    return task;
  }

  private async _doRefresh(): Promise<void> {
    try {
      const running = await KernelAPI.listRunning(this._serverSettings);
      if (this._disposed) return;
      const knownIds = new Set(this._kernels.keys());

      // Add newly discovered kernels
      for (const model of running) {
        if (!knownIds.has(model.id)) {
          this._kernels.set(model.id, {
            client: null as unknown as JupyterClient,
            info: {
              id: model.id,
              name: model.name,
              displayName: `${model.name} (${model.id.slice(0, 4)})`,
              status: 'idle',
              connectionStatus: 'disconnected',
              consumers: new Set(),
              startedAt: Date.now(),
            },
          });
        }
      }

      // Remove kernels that are no longer running on the server
      const runningIds = new Set(running.map((k) => k.id));
      for (const id of knownIds) {
        if (!runningIds.has(id)) {
          const entry = this._kernels.get(id);
          if (entry?.client) {
            entry.client.detach();
          }
          this._kernels.delete(id);
        }
      }

      this._notifyListeners();
    } catch (err) {
      if (isJupyter401(err)) {
        Sentry.addBreadcrumb({
          category: 'jupyter',
          message: 'kernel list refresh failed with 401',
          level: 'warning',
        });
      }
      console.warn('[kernel-pool] Failed to refresh kernel list:', err);
    }
  }

  /**
   * Start a new kernel and return a KernelHandle for it.
   *
   * The consumerId identifies who requested the kernel (e.g. 'notebook', 'antioch-agent').
   * An optional displayName gives the kernel a human-readable label in the UI.
   */
  async startKernel(
    consumerId: string,
    kernelspec: string = 'antioch-sim',
    displayName?: string
  ): Promise<KernelHandle> {
    if (this._disposed) throw new Error('KernelPool is disposed');

    let client = new JupyterClient(this._serverSettings.baseUrl, this._token);
    let kernelId: string;
    try {
      kernelId = await client.startNewKernel(kernelspec);
    } catch (err) {
      if (isJupyter401(err) && this._onTokenRefreshNeeded) {
        Sentry.addBreadcrumb({
          category: 'jupyter',
          message: 'kernel start failed with 401 -- attempting token refresh',
          level: 'warning',
          data: { consumerId, kernelspec },
        });
        const freshToken = await this._onTokenRefreshNeeded();
        this.updateToken(freshToken);
        client = new JupyterClient(this._serverSettings.baseUrl, this._token);
        try {
          kernelId = await client.startNewKernel(kernelspec);
        } catch (retryErr) {
          if (isJupyter401(retryErr)) {
            Sentry.addBreadcrumb({
              category: 'jupyter',
              message: 'kernel start retry after token refresh still failed with 401',
              level: 'warning',
              data: { consumerId, kernelspec },
            });
            throw new Error(
              'Jupyter authentication expired. The workspace token may have been refreshed -- please retry the operation'
            );
          }
          throw retryErr;
        }
      } else if (isJupyter401(err)) {
        throw new Error(
          'Jupyter authentication expired. The workspace token may have been refreshed -- please retry the operation'
        );
      } else {
        throw err;
      }
    }

    const info: KernelInfo = {
      id: kernelId,
      name: kernelspec,
      displayName: displayName ?? `Kernel ${this._kernels.size + 1}`,
      status: 'idle',
      connectionStatus: 'connected',
      consumers: new Set([consumerId]),
      startedAt: Date.now(),
    };

    this._kernels.set(kernelId, { client, info });

    // Track health changes
    client.onKernelHealthChange((health) => {
      const entry = this._kernels.get(kernelId);
      if (entry) {
        entry.info.status = health.status;
        entry.info.connectionStatus = health.connectionStatus;
        this._notifyListeners();
      }
    });

    this._notifyListeners();

    return new KernelHandle(kernelId, consumerId, client, this);
  }

  /**
   * Connect to an existing kernel and return a KernelHandle.
   *
   * If another consumer already has a client connected to this kernel,
   * a new JupyterClient is created to connect separately.
   */
  async connectToKernel(kernelId: string, consumerId: string): Promise<KernelHandle> {
    if (this._disposed) throw new Error('KernelPool is disposed');

    const existing = this._kernels.get(kernelId);

    if (existing?.client?.isConnected) {
      // Reuse existing client, just add consumer
      existing.info.consumers.add(consumerId);
      this._notifyListeners();
      return new KernelHandle(kernelId, consumerId, existing.client, this);
    }

    // Create a new client for this kernel
    const client = new JupyterClient(this._serverSettings.baseUrl, this._token);
    try {
      await client.connectToKernel(kernelId, existing?.info.name ?? 'antioch-sim');
    } catch (err) {
      if (isJupyter401(err)) {
        Sentry.addBreadcrumb({
          category: 'jupyter',
          message: 'kernel connect failed with 401 -- workspace token may be expired',
          level: 'warning',
          data: { kernelId, consumerId },
        });
        throw new Error(
          'Jupyter authentication expired. The workspace token may have been refreshed -- please retry the operation'
        );
      }
      // Kernel died between UI list refresh and this connect attempt. Resync
      // the pool so the stale kernel disappears from the consumer list, then
      // rethrow the typed error for callers to surface a user-friendly message.
      if (err instanceof KernelNotFoundError) {
        Sentry.addBreadcrumb({
          category: 'jupyter',
          message: 'attempted to attach to a kernel that no longer exists on the server',
          level: 'info',
          data: { kernelId, consumerId },
        });
        this._kernels.delete(kernelId);
        this._notifyListeners();
        // refresh() swallows its own errors via _doRefresh's try/catch, but
        // wrap defensively so any unexpected throw cannot mask the typed
        // KernelNotFoundError the caller is relying on for UX
        try {
          await this.refresh();
        } catch (refreshErr) {
          Sentry.addBreadcrumb({
            category: 'jupyter',
            message: 'kernel pool refresh failed after stale-kernel detection; rethrowing original KernelNotFoundError',
            level: 'warning',
            data: { kernelId, consumerId, refreshError: String(refreshErr) },
          });
        }
        throw err;
      }
      throw err;
    }

    if (existing) {
      existing.client = client;
      existing.info.consumers.add(consumerId);
      existing.info.connectionStatus = 'connected';
    } else {
      this._kernels.set(kernelId, {
        client,
        info: {
          id: kernelId,
          name: 'antioch-sim',
          displayName: `antioch-sim (${kernelId.slice(0, 4)})`,
          status: 'idle',
          connectionStatus: 'connected',
          consumers: new Set([consumerId]),
          startedAt: Date.now(),
        },
      });
    }

    client.onKernelHealthChange((health) => {
      const entry = this._kernels.get(kernelId);
      if (entry) {
        entry.info.status = health.status;
        entry.info.connectionStatus = health.connectionStatus;
        this._notifyListeners();
      }
    });

    this._notifyListeners();
    return new KernelHandle(kernelId, consumerId, client, this);
  }

  /**
   * Get or create a kernel bound to a Jupyter session keyed by `sessionPath`.
   *
   * Jupyter sessions are durable server-side objects identified by their
   * path. Two calls with the same path return a handle to the same
   * underlying kernel even across page reloads, which is what gives the
   * agent a stable kernel identity per chat.
   *
   * If a session with this path already exists, we connect to its kernel.
   * If not, we create a new session (which starts a kernel) via the Jupyter
   * Session API. We do not shut down other sessions or rebind kernels --
   * that is left to higher-level flows.
   */
  async ensureSessionKernel(
    sessionPath: string,
    consumerId: string,
    kernelspec: string = 'antioch-sim',
    displayName?: string
  ): Promise<KernelHandle> {
    if (this._disposed) throw new Error('KernelPool is disposed');

    const resolvedDisplayName = displayName ?? `Session ${sessionPath}`;

    const runSessionFlow = async (): Promise<KernelHandle> => {
      const sessions = await SessionAPI.listRunning(this._serverSettings);
      const existing = sessions.find((s) => s.path === sessionPath && s.kernel);

      if (existing?.kernel) {
        const handle = await this.connectToKernel(existing.kernel.id, consumerId);
        const entry = this._kernels.get(existing.kernel.id);
        if (entry && displayName) {
          entry.info.displayName = displayName;
          this._notifyListeners();
        }
        return handle;
      }

      const created = await SessionAPI.startSession(
        {
          path: sessionPath,
          name: sessionPath,
          type: 'console',
          kernel: { name: kernelspec },
        },
        this._serverSettings
      );

      if (!created.kernel) {
        throw new Error(`Session created for '${sessionPath}' returned no kernel`);
      }

      const handle = await this.connectToKernel(created.kernel.id, consumerId);
      const entry = this._kernels.get(created.kernel.id);
      if (entry) {
        entry.info.displayName = resolvedDisplayName;
        entry.info.name = kernelspec;
        this._notifyListeners();
      }
      return handle;
    };

    try {
      return await runSessionFlow();
    } catch (err) {
      if (isJupyter401(err) && this._onTokenRefreshNeeded) {
        Sentry.addBreadcrumb({
          category: 'jupyter',
          message: 'session kernel lookup failed with 401 -- attempting token refresh',
          level: 'warning',
          data: { sessionPath, consumerId },
        });
        const freshToken = await this._onTokenRefreshNeeded();
        this.updateToken(freshToken);
        try {
          return await runSessionFlow();
        } catch (retryErr) {
          if (isJupyter401(retryErr)) {
            throw new Error(
              'Jupyter authentication expired. The workspace token may have been refreshed -- please retry the operation'
            );
          }
          throw retryErr;
        }
      }
      if (isJupyter401(err)) {
        throw new Error(
          'Jupyter authentication expired. The workspace token may have been refreshed -- please retry the operation'
        );
      }
      throw err;
    }
  }

  /**
   * Shut down the Jupyter session bound to `sessionPath` and its kernel.
   *
   * Used by the agent chat to reset the kernel for a session without
   * abandoning the path: after this call, a subsequent
   * `ensureSessionKernel(sessionPath, ...)` starts a fresh kernel at the
   * same path. Best-effort: if no session matches the path, this is a
   * no-op. The pool's local kernel entry is also dropped so listeners
   * see the removal.
   */
  async shutdownSession(sessionPath: string): Promise<void> {
    if (this._disposed) return;

    let sessions: Awaited<ReturnType<typeof SessionAPI.listRunning>>;
    try {
      sessions = await SessionAPI.listRunning(this._serverSettings);
    } catch {
      return;
    }

    const match = sessions.find((s) => s.path === sessionPath);
    if (!match) return;

    const kernelId = match.kernel?.id;
    try {
      await SessionAPI.shutdownSession(match.id, this._serverSettings);
    } catch {
      // Best-effort; the kernel may already be gone.
    }

    if (kernelId) {
      const entry = this._kernels.get(kernelId);
      if (entry?.client) {
        try {
          await entry.client.disconnect();
        } catch {
          entry.client.detach();
        }
      }
      this._kernels.delete(kernelId);
      this._notifyListeners();
    }
  }

  /** Shut down a specific kernel */
  async shutdownKernel(kernelId: string): Promise<void> {
    const entry = this._kernels.get(kernelId);
    if (entry) {
      if (entry.client) {
        try {
          await entry.client.disconnect();
        } catch {
          entry.client.detach();
        }
      }
      this._kernels.delete(kernelId);
      this._notifyListeners();
    } else {
      try {
        await KernelAPI.shutdownKernel(kernelId, this._serverSettings);
      } catch {
        // Best-effort
      }
    }
  }

  /** Shut down all kernels */
  async shutdownAll(): Promise<number> {
    const count = this._kernels.size;
    const ids = Array.from(this._kernels.keys());
    for (const id of ids) {
      await this.shutdownKernel(id);
    }
    return count;
  }

  /** Remove a consumer from a kernel without killing it */
  detachConsumer(kernelId: string, consumerId: string): void {
    const entry = this._kernels.get(kernelId);
    if (entry) {
      entry.info.consumers.delete(consumerId);
      this._notifyListeners();
    }
  }

  /** Subscribe to kernel list changes */
  onKernelsChanged(listener: KernelsChangedListener): () => void {
    this._listeners.add(listener);
    listener(this.listKernels());
    return () => this._listeners.delete(listener);
  }

  /** Clean up all resources, shutting down kernels on the Jupyter server */
  dispose(): void {
    this._disposed = true;
    for (const [id, entry] of this._kernels) {
      if (entry.client) {
        entry.client.disconnect().catch(() => entry.client.detach());
      } else {
        KernelAPI.shutdownKernel(id, this._serverSettings).catch(() => {});
      }
    }
    this._kernels.clear();
    this._listeners.clear();
  }

  private _notifyListeners(): void {
    const kernels = this.listKernels();
    for (const listener of this._listeners) {
      listener(kernels);
    }
  }
}

/** Check whether an error is a Jupyter 401 authentication failure */
export function isJupyter401(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const resp = (err as { response?: Response }).response;
  if (resp && resp.status === 401) return true;
  return /Invalid response:\s*401(?:\D|$)/.test(err.message);
}

/**
 * A handle to a single kernel held by a consumer.
 *
 * Provides execution and file I/O scoped to that kernel.
 * Detaching releases the consumer's claim without killing the kernel.
 */
export class KernelHandle {
  readonly kernelId: string;
  readonly consumerId: string;
  private _client: JupyterClient;
  private _pool: KernelPool;
  private _detached = false;

  constructor(kernelId: string, consumerId: string, client: JupyterClient, pool: KernelPool) {
    this.kernelId = kernelId;
    this.consumerId = consumerId;
    this._client = client;
    this._pool = pool;
  }

  /**
   * Access the underlying JupyterClient for operations not exposed
   * directly on KernelHandle (notebook I/O, widgets, subscriptions).
   */
  get client(): JupyterClient {
    return this._client;
  }

  get isConnected(): boolean {
    return !this._detached && this._client.isConnected;
  }

  get health(): KernelHealth {
    return this._client.kernelHealth;
  }

  get serverSettings(): ServerConnection.ISettings {
    return this._client.serverSettings;
  }

  onHealthChange(listener: KernelHealthListener): () => void {
    return this._client.onKernelHealthChange(listener);
  }

  async executeCode(
    code: string,
    silent?: boolean,
    raw?: boolean,
    onOutput?: (output: { type: string; content: unknown }) => void
  ): ReturnType<JupyterClient['executeCode']> {
    this._assertConnected();
    return this._client.executeCode(code, silent, raw, onOutput);
  }

  async readFile(path: string): Promise<string> {
    this._assertConnected();
    return this._client.readFile(path);
  }

  async saveFile(path: string, content: string): Promise<void> {
    this._assertConnected();
    return this._client.saveFile(path, content);
  }

  async fileExists(path: string): Promise<boolean> {
    this._assertConnected();
    return this._client.fileExists(path);
  }

  async restartKernel(): Promise<void> {
    this._assertConnected();
    return this._client.restartKernel();
  }

  async waitForIdle(): Promise<void> {
    this._assertConnected();
    return this._client.waitForIdle();
  }

  async shutdownAllKernels(): Promise<number> {
    this._assertConnected();
    return this._client.shutdownAllKernels();
  }

  /** Release this consumer's claim on the kernel without killing it */
  detach(): void {
    if (this._detached) return;
    this._detached = true;
    this._pool.detachConsumer(this.kernelId, this.consumerId);
  }

  private _assertConnected(): void {
    if (this._detached) throw new Error('KernelHandle has been detached');
    if (!this._client.isConnected) throw new Error('Kernel is not connected');
  }
}
